import moment from "moment";
import isEmpty from "lodash/isEmpty";
import dateTimeUtils from "./dateTimeUtils";

export const operators = {
  equal: "eq",
  notEqual: "ne",
  greaterThan: "gt",
  greaterThanOrEqual: "ge",
  lessThan: "lt",
  lessThanOrEqual: "le",
  or: "or",
  range: "range",
  isBetween: "isBetween",
  isNotBetween: "isNotBetween",
  isOnOrAfter: "isOnOrAfter",
  isOnOrBefore: "isOnOrBefore",
  isWithinLastDays: "isWithinLastDays",
  notIn: Symbol("notIn"),
  any: "any",
  ids: "ids",
};

type OperatorKeys = keyof typeof operators;
type OperatorValues = (typeof operators)[OperatorKeys];
type CollectionKey = { collectionName: string; propName: string };
export type RangeFilterValues = { from: string; to: string };
export type NumberRangeFilterValues = { start: string; finish: string };
export type FilterOption = {
  key: string;
  operator: OperatorValues;
  settings?: CollectionKey;
};
type FilterOptions = Array<FilterOption>;
export type FilterParamValue = string | number | RangeFilterValues;
export type FilterEntity = FilterParamValue | FilterParamValue[] | RangeFilterValues | FilterOperatorValues[] | {};
/**
 * @deprecated use GenericFiltersMap instead
 */
export type FiltersMap = { [key: string]: FilterEntity | null };
export type FilterOperatorValues = {
  values: FilterParamValue[];
  operator: OperatorKeys;
};

export enum FilterType {
  DateRange,
  Array,
  Value,
}
export type FilterTypesMap<T extends string | number> = { [key in T]: FilterType };
export type GenericFiltersMap<T extends string | number> = { [key in T]?: any };

function buildFilterQuery<T extends GenericFiltersMap<string> | string = GenericFiltersMap<string>>(
  this: { filterOptions: FilterOptions },
  filters: GenericFiltersMap<string> | null,
): T {
  const filterOptionsMap = new Map(this.filterOptions.map((option) => [option.key, option]));

  const idFilters: Record<string, unknown> = {};
  const standardFilters: string[] = [];
  let hasIdFilters = false;

  if (filters) {
    for (const key of Object.keys(filters)) {
      const option = filterOptionsMap.get(key);
      if (!option) continue;

      const value = filters[key];

      if (option.operator === operators.ids) {
        idFilters[key] = value;
        hasIdFilters = true;
      } else {
        standardFilters.push(new Operator(option).buildFilter(value));
      }
    }
  }

  const standardFilterString = standardFilters.join(" and ");

  if (hasIdFilters) {
    const result: GenericFiltersMap<string> = {
      ...idFilters,
      ...(standardFilterString ? { filter: standardFilterString } : {}),
    };
    return result as T;
  }

  return standardFilterString as T;
}

function buildODataFilterQuery(this: { filterOptions: FilterOptions }, filters: GenericFiltersMap<string>) {
  let filterQuery: GenericFiltersMap<string>[] = [];

  for (let key in filters) {
    let filterOption = this.filterOptions.find((f) => f.key === key);

    if (filterOption) {
      filterQuery.push(new Operator(filterOption).buildODataFilter(filters[key]));
    }
  }

  return { and: filterQuery };
}

interface IFilter {
  filterOptions: FilterOptions;
  buildFilterQuery: typeof buildFilterQuery;
  buildODataFilterQuery: typeof buildODataFilterQuery;
  buildFilterQueryMultOperators: typeof buildFilterQueryMultOperators;
}

export class Filter implements IFilter {
  filterOptions: FilterOptions;

  constructor(filterOptions: FilterOptions) {
    this.filterOptions = filterOptions;
  }

  buildFilterQuery = buildFilterQuery;
  buildODataFilterQuery = buildODataFilterQuery;
  buildFilterQueryMultOperators = buildFilterQueryMultOperators;
}

export function calculateAppliedFilters(appliedFilters: GenericFiltersMap<string>) {
  let newAppliedFiltersCount = 0;

  for (let filterName in appliedFilters) {
    let filter = appliedFilters[filterName];

    if (Array.isArray(filter)) {
      newAppliedFiltersCount += filter.length;
    } else {
      newAppliedFiltersCount += 1;
    }
  }

  return newAppliedFiltersCount;
}

export function formatFilterParams(
  appliedFilters: GenericFiltersMap<string> | null,
  filterParameters: FilterOptions | {},
) {
  return getFilterQuery(appliedFilters, filterParameters, buildFilterQuery);
}

export function formatFilterParamsMultOperators(appliedFilters: GenericFiltersMap<string> | null) {
  return getFilterQueryMultOperators(appliedFilters, buildFilterQueryMultOperators);
}

export function formatODataFilterParams(
  appliedFilters: GenericFiltersMap<string> | null,
  filterParameters: FilterOptions | {},
) {
  return getFilterQuery(appliedFilters, filterParameters, buildODataFilterQuery);
}

function getFilterQuery(
  appliedFilters: GenericFiltersMap<string> | null,
  filterParameters: FilterOptions | {},
  formatFunc: (filters: GenericFiltersMap<string>) => string | GenericFiltersMap<string>,
) {
  if (isEmpty(appliedFilters)) {
    return null;
  }

  const filter = new Filter(filterParameters as FilterOptions);
  return formatFunc.call(filter, appliedFilters);
}

export function getFilterQueryMultOperators(
  appliedFilters: GenericFiltersMap<string> | null,
  formatFunc: (filters: GenericFiltersMap<string>) => string,
) {
  if (isEmpty(appliedFilters)) {
    return null;
  }

  return formatFunc(appliedFilters);
}

function buildFilterQueryMultOperators(filtersMap: GenericFiltersMap<string>) {
  let filterQuery = "";

  for (let key in filtersMap) {
    const filters = filtersMap[key] as Array<FilterOperatorValues>;

    for (let filterKey in filters) {
      const filterParams = filters[filterKey];
      const filterOption = { key, operator: filterParams.operator } as FilterOption;

      if (filterOption) {
        if (filterQuery) {
          filterQuery = `${filterQuery} and `;
        }

        filterQuery += new Operator(filterOption).buildFilter(filterParams.values);
      }
    }
  }

  return filterQuery;
}

class Operator {
  private readonly option: FilterOption;

  constructor(option: FilterOption) {
    this.option = option;
  }

  buildFilter(value: unknown) {
    switch (this.option.operator) {
      case operators.or:
        return this.buildOrFilter(this.option.key, value as unknown[]);
      case operators.range:
        return this.buildRangeFilter(value as RangeFilterValues);
      case operators.isBetween:
        return this.buildIsBetweenFilter(value as RangeFilterValues[]);
      case operators.isNotBetween:
        return this.buildIsNotBetweenFilter(value as RangeFilterValues[]);
      case operators.notIn:
        return this.buildNotInFilter(value as string[]);
      case operators.any:
        return this.buildAnyFilter(value as unknown[]);
      case operators.isOnOrAfter:
        return this.buildIsOnOrAfterFilter(this.option.key, value as string[]);
      case operators.isOnOrBefore:
        return this.buildIsOnOrBeforeFilter(this.option.key, value as string[]);
      case operators.isWithinLastDays:
        return this.buildIsWithinLastDaysFilter(value as NumberRangeFilterValues[]);
      default:
        return `${this.option.key} ${this.option.operator as string} ${this.valueFormater(value)}`;
    }
  }

  buildODataFilter(value: unknown | unknown[]) {
    if (this.option.operator === operators.or) {
      return this.buildOrODataFilter(this.option.key, value as unknown[]);
    }

    return { [this.option.key]: value };
  }

  private buildRangeFilter(values: RangeFilterValues) {
    const name = this.option.key;
    let query = "";

    if (values.from) {
      query = `${name} ${operators.greaterThanOrEqual} ${moment.utc(values.from).toISOString(false)}`;
    }

    if (values.to) {
      if (query) {
        query = `${query} and `;
      }

      query = `${query}${name} ${operators.lessThanOrEqual} ${moment
        .utc(values.to, "MM/DD/YYYY")
        .add(1, "days")
        .toISOString(false)}`;
    }

    query = `(${query})`;

    return query;
  }

  private buildNotInRangeFilter(values: RangeFilterValues) {
    const name = this.option.key;
    let query = "";

    if (values.from) {
      query = `${name} ${operators.lessThan} ${moment.utc(values.from).toISOString(false)}`;
    }

    if (values.to) {
      if (query) {
        query = `${query} or `;
      }

      query = `${query}${name} ${operators.greaterThan} ${moment
        .utc(values.to, "MM/DD/YYYY")
        .add(1, "days")
        .toISOString(false)}`;
    }

    query = `(${query})`;

    return query;
  }

  private buildOrFilter(name: string, values: unknown[]) {
    let query = "";

    values.forEach((value) => {
      if (query) {
        query = `${query} or `;
      }

      query = `${query}${name} eq ${this.valueFormater(value)}`;
    });

    query = `(${query})`;

    return query;
  }

  private buildNotInFilter(values: string[]) {
    const name = this.option.key;
    const res = values.reduce((query, value) => {
      const queryConnector = query ? `${query} and ` : "";
      return `${queryConnector}${name} ne ${this.valueFormater(value)}`;
    }, "");
    return `(${res})`;
  }

  private buildAnyFilter(values: unknown[]) {
    if (!this.option.settings) {
      return "";
    }

    const { collectionName, propName } = this.option.settings;
    const objectName = "x";
    const condition = this.buildOrFilter(`${objectName}/${propName}`, values);

    return `${collectionName}/any(${objectName}: ${condition})`;
  }

  private valueFormater(value: unknown) {
    if (typeof value == "number" || value instanceof Date || typeof value == "boolean") {
      return value;
    }

    return `"${value}"`;
  }

  private buildOrODataFilter(name: string, values: unknown[]) {
    const query = values.map((value) => ({ [name]: value }));
    return { or: query };
  }

  private buildIsBetweenFilter(values: RangeFilterValues[]) {
    let query = "";

    values.forEach((value) => {
      if (query) {
        query = `${query} or `;
      }

      query = `${query}${this.buildRangeFilter(value)}`;
    });

    return query;
  }

  private buildIsWithinLastDaysFilter(values: NumberRangeFilterValues[]) {
    let filterValues: RangeFilterValues[] = [];

    values.forEach((item) => {
      const start = dateTimeUtils.toLocalFormatDateTime(dateTimeUtils.dateFromNow(-item.start), "MM/DD/YYYY");
      const finish = dateTimeUtils.toLocalFormatDateTime(dateTimeUtils.dateFromNow(-item.finish), "MM/DD/YYYY");

      filterValues.push({
        from: finish,
        to: start,
      });
    });

    return this.buildIsBetweenFilter(filterValues);
  }

  private buildIsNotBetweenFilter(values: RangeFilterValues[]) {
    let query = "";

    values.forEach((value) => {
      if (query) {
        query = `${query} or `;
      }

      query = `${query}${this.buildNotInRangeFilter(value)}`;
    });

    return query;
  }

  private buildIsOnOrAfterFilter(name: string, values: string[]) {
    let query = "";
    values.forEach((value) => {
      if (query) {
        query = `${query} or `;
      }
      query = query = `${query}(${name} ge ${moment.utc(value).toISOString(false)})`;
    });

    return query;
  }

  private buildIsOnOrBeforeFilter(name: string, values: string[]) {
    let query = "";

    values.forEach((value) => {
      if (query) {
        query = `${query} or `;
      }

      dateTimeUtils.toEndDateTime(value);
      query = query = `${query}(${name} le ${dateTimeUtils.toEndDateTime(value)})`;
    });

    return query;
  }
}

export const mapFilterOption = <T>(
  filterOptions: GenericFiltersMap<string>,
  data: T[],
  propertyName: string,
  dataItemMapping: (item: T) => FilterEntity,
) => {
  return {
    ...filterOptions,
    [propertyName]: data.map((item) => dataItemMapping(item)),
  };
};

export const mapRangeToString = (range: RangeFilterValues): string => {
  return `${dateTimeUtils.toServerFormatDate(new Date(range.from))}, ${dateTimeUtils.toServerFormatDate(
    new Date(range.to),
  )}`;
};

export const mapIntRangeToString = (range: NumberRangeFilterValues): string => {
  if (range.start === "0") {
    return `${range.finish}`;
  }

  return `${range.start}, ${range.finish}`;
};

export const mapStringToRange = (value: string): RangeFilterValues => {
  const from = dateTimeUtils.toLocalFormatDateTime(new Date(value.split(",")[0]), "MM/DD/YYYY");
  const to = dateTimeUtils.toLocalFormatDateTime(new Date(value.split(",")[1]), "MM/DD/YYYY");
  return { from, to };
};

export const mapStringToIntRange = (value: string): NumberRangeFilterValues => {
  if (value.split(",").length > 1) {
    const start = value.split(",")[0];
    const finish = value.split(",")[1];
    return { start, finish };
  } else {
    const start = "0";
    const finish = value;
    return { start, finish };
  }
};
